import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, resolveMediaUrl } from "@/lib/api";

const ANIM_CLASS = {
  float: "deco-float",
  swing: "deco-swing",
  spin: "deco-spin",
  pulse: "deco-pulse",
};

/** Un élément déco positionné en % du conteneur. */
export function DecoItem({ d, onPointerDown, selected }) {
  const style = {
    left: `${d.x_pct}%`,
    top: `${d.y_pct}%`,
    width: `${d.width_pct}%`,
    opacity: d.opacity,
    transform: "translate(-50%, -50%)",
  };
  return (
    <div
      className={"absolute " + (onPointerDown ? "cursor-grab touch-none" : "")}
      style={style}
      onPointerDown={onPointerDown}
      data-testid={`deco-item-${d.id}`}
    >
      <div className={ANIM_CLASS[d.animation] || ""}>
        <img
          src={resolveMediaUrl(d.image_url)}
          alt=""
          draggable={false}
          className={"block w-full select-none " + (selected ? "outline outline-2 outline-offset-2 outline-[#FF3B3B] rounded" : "")}
          style={{ transform: `rotate(${d.rotation || 0}deg) scaleX(${d.flip ? -1 : 1})` }}
        />
      </div>
    </div>
  );
}

/** Pluie d'images (animation « tomber »). */
export function DecoFall({ d }) {
  const flakes = useMemo(
    () => Array.from({ length: 14 }, (_, i) => ({
      left: (i * 73) % 100,
      delay: -((i * 1.7) % 9),
      dur: 7 + ((i * 2.3) % 6),
      scale: 0.6 + ((i * 37) % 50) / 100,
    })),
    []
  );
  return (
    <>
      {flakes.map((f, i) => (
        <img
          key={i}
          src={resolveMediaUrl(d.image_url)}
          alt=""
          draggable={false}
          className="deco-fall absolute top-0 select-none"
          style={{
            left: `${f.left}%`,
            width: `${d.width_pct * f.scale}%`,
            opacity: d.opacity,
            animationDelay: `${f.delay}s`,
            animationDuration: `${f.dur}s`,
          }}
        />
      ))}
    </>
  );
}

export function DecorationsCanvas({ items }) {
  return (
    <>
      {items.map((d) => (d.animation === "fall" ? <DecoFall key={d.id} d={d} /> : <DecoItem key={d.id} d={d} />))}
    </>
  );
}

const SCREEN_BY_PATH = { "/dashboard/client": "client", "/dashboard/driver": "driver" };

/** Calque global : affiche les décos actives de l'accueil courant (mis à jour en direct). */
export default function LiveDecorations() {
  const { pathname } = useLocation();
  const screen = SCREEN_BY_PATH[pathname.replace(/\/$/, "")];
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    if (!screen) return;
    try {
      const r = await api.get(`/price/decorations?screen=${screen}`);
      setItems(r.data?.items || []);
    } catch { /* noop */ }
  }, [screen]);

  useEffect(() => {
    if (!screen) { setItems([]); return undefined; }
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [screen, load]);

  if (!screen || items.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[45] overflow-hidden" aria-hidden="true" data-testid="live-decorations">
      <div className="relative mx-auto h-full w-full max-w-md">
        <DecorationsCanvas items={items} />
      </div>
    </div>
  );
}
